import { useQuery } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { api } from "../lib/api/endpoints";
import type { Space, SpaceKind } from "../lib/api/models";
import { qk, useSpaces } from "../lib/queries";

/** The areas of the rail that hold documents: Clients · Handbook (the internal spaces). Templates live in Admin. */
export type Section = "clients" | "internal";

export type SectionState = {
  /** The rail area the page belongs to; on search, new-document and admin pages the last one visited. */
  section: Section;
  /** True on the admin pages: the rail marks Admin and the column lists the admin sections. */
  admin: boolean;
  /** The open client binder, or the client of the open document. */
  clientId: string | undefined;
  /** The space whose document tree the left column shows. */
  space: Space | null;
  /** The open document's id (resolved from a code when the URL carries one). */
  activeDocId: string | undefined;
};

const sectionOf = (kind: SpaceKind): Section | undefined =>
  kind === "client" ? "clients" : kind === "internal" ? "internal" : undefined;

export function useSection(): SectionState {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { data: spaces = [] } = useSpaces();
  const part = (re: RegExp) => {
    const m = re.exec(pathname)?.[1];
    return m ? decodeURIComponent(m) : undefined;
  };
  const docRef = part(/^\/d\/([^/]+)/);
  const routeClientId = part(/^\/clients\/([^/]+)/);
  const slug = part(/^\/topics\/([^/]+)/);
  // Shares the document page's cache entry, so this costs no extra request.
  const { data: doc } = useQuery({
    queryKey: qk.doc(docRef ?? ""),
    queryFn: () => api.documents.get(docRef ?? ""),
    enabled: Boolean(docRef),
    retry: false,
  });

  const fromRoute: Space | undefined = slug
    ? spaces.find((s) => s.slug === slug)
    : routeClientId
      ? spaces.find((s) => s.id === routeClientId)
      : pathname === "/admin/templates"
        ? spaces.find((s) => s.kind === "templates")
        : doc
          ? spaces.find((s) => s.id === doc.spaceId)
          : undefined;
  const routeSection: Section | undefined = pathname.startsWith("/clients")
    ? "clients"
    : pathname === "/handbook"
      ? "internal"
      : fromRoute
        ? sectionOf(fromRoute.kind)
        : undefined;

  // Search, new-document and admin pages keep the column of the last section and space.
  const [last, setLast] = useState<{ section: Section; spaceId: string | null }>({ section: "clients", spaceId: null });
  useEffect(() => {
    if (routeSection) setLast({ section: routeSection, spaceId: fromRoute?.id ?? null });
  }, [routeSection, fromRoute]);

  const section = routeSection ?? last.section;
  const inTemplates = fromRoute?.kind === "templates";
  const space = routeSection || inTemplates ? (fromRoute ?? null) : (spaces.find((s) => s.id === last.spaceId) ?? null);
  return {
    section,
    // A template document belongs to Admin: the rail marks Admin although the URL is /d/<code>.
    admin: pathname.startsWith("/admin") || inTemplates,
    clientId: routeClientId ?? (space?.kind === "client" ? space.id : undefined),
    space,
    activeDocId: docRef ? doc?.id : undefined,
  };
}
